/** Express Entry → files: list and creation with the « profil express » (§2 A). */

import {
  useCrmContactsList,
  useImmigrationDossiersCreate,
  useImmigrationDossiersExtractCreate,
  useImmigrationDossiersList,
} from "@agencyos/api-client";
import { formatPhone } from "@agencyos/core";
import { Avatar, Badge, Button, EmptyState, GlassCard, SelectField, SkeletonText, TextAreaField, TextField } from "@agencyos/ui-web";
import { FileCheck2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Page } from "../components/AppShell";
import { fieldErrors } from "../lib/settings";
import { Pager } from "./billing/shared";

type Proposal = Record<string, unknown>;

function phoneText(phone?: string | null): string {
  if (!phone) return "";
  try {
    return formatPhone(phone);
  } catch {
    return phone;
  }
}

/** Proposal from the AI → profile structure of the API (nothing is invented here either). */
export function profileFromProposal(p: Proposal): Record<string, unknown> {
  const scores = (p.language_scores ?? {}) as Record<string, number>;
  const principal: Record<string, unknown> = {};
  if (typeof p.age === "number") principal.age = p.age;
  if (typeof p.education === "string") principal.education = p.education;
  if (typeof p.foreign_experience_years === "number") principal.foreign_experience_years = p.foreign_experience_years;
  if (typeof p.canadian_experience_years === "number") principal.canadian_experience_years = p.canadian_experience_years;
  if (typeof p.occupation === "string") principal.occupation = p.occupation;
  if (typeof p.language_test === "string" && Object.keys(scores).length) {
    principal.first_language = { test: p.language_test, raw_scores: scores, clb: {} };
  }
  const profile: Record<string, unknown> = { principal };
  for (const key of ["marital_status", "has_spouse", "spouse_accompanying", "children"]) {
    if (p[key] !== undefined) profile[key] = p[key];
  }
  if (typeof p.spouse_education === "string") profile.spouse = { education: p.spouse_education };
  return profile;
}

function NewDossier() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [contact, setContact] = useState("");
  const [text, setText] = useState("");
  const contacts = useCrmContactsList(search.trim().length >= 2 ? { q: search.trim() } : undefined);
  const extract = useImmigrationDossiersExtractCreate();
  const create = useImmigrationDossiersCreate();
  const proposal = extract.data?.status === 200 ? (extract.data.data as { proposal: Proposal; questions: string[] }) : null;
  const errors = fieldErrors(extract.error ?? create.error);
  const options = contacts.data?.status === 200 ? contacts.data.data.results : [];

  const onCreate = (event: FormEvent) => {
    event.preventDefault();
    if (!contact) return;
    create.mutate(
      { data: { contact, profile: proposal ? profileFromProposal(proposal.proposal) : {} } },
      { onSuccess: (r) => r.status === 201 && navigate(`/dossiers/${r.data.id}`) },
    );
  };

  return (
    <GlassCard as="section" className="stack">
      <h2>{t("ee.new")}</h2>
      <form className="stack" onSubmit={onCreate} noValidate>
        <TextField label={t("ee.contact")} type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <SelectField
          label={t("ee.pickContact")}
          value={contact}
          options={[{ value: "", label: "—" }, ...options.map((c) => ({ value: c.id, label: `${c.full_name || "?"} · ${phoneText(c.phone_e164)}` }))]}
          onChange={(e) => setContact(e.target.value)}
        />
        <TextAreaField label={t("ee.express")} hint={t("ee.expressHint")} rows={4} value={text} error={errors.non_field_errors} onChange={(e) => setText(e.target.value)} />
        <div className="row">
          <Button variant="secondary" disabled={!text.trim() || extract.isPending} onClick={() => extract.mutate({ data: { text } })}>
            {extract.isPending ? t("common.loading") : t("ee.analyse")}
          </Button>
        </div>
        {extract.isError ? <p role="alert" className="ko">{Object.values(errors)[0] ?? t("common.error")}</p> : null}
        {proposal ? (
          <div className="grid-2">
            <div className="installment-row stack">
              <h3>{t("ee.proposal")}</h3>
              <ul className="plain-list">
                {Object.entries(proposal.proposal).map(([k, v]) => (
                  <li key={k}>
                    <strong>{k}</strong> : {typeof v === "object" ? JSON.stringify(v) : String(v)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="installment-row stack">
              <h3>{t("ee.questions")}</h3>
              <ol>
                {proposal.questions.map((q) => (
                  <li key={q} lang="fr">
                    {q}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={!contact || create.isPending}>
            {t("ee.create")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

export function DossiersPage() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const dossiers = useImmigrationDossiersList({ page });
  const items = dossiers.data?.status === 200 ? dossiers.data.data.results : [];
  const hasNext = dossiers.data?.status === 200 && Boolean(dossiers.data.data.next);
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  return (
    <Page title={t("ee.title")} description={t("ee.intro")}>
      <div className="bento">
        <GlassCard as="section" className="stack bento__wide" aria-label={t("ee.title")}>
          {dossiers.isPending ? <SkeletonText lines={5} /> : null}
          {!dossiers.isPending && !items.length ? (
            <EmptyState icon={<FileCheck2 size={22} strokeWidth={1.75} />} title={t("ee.none")} />
          ) : null}
          <ul className="plain-list file-list">
            {items.map((d) => (
              <li key={d.id}>
                <Link to={`/dossiers/${d.id}`} className="file-row">
                  <Avatar name={d.contact_name || d.contact_phone} />
                  <span className="file-row__name">
                    <strong>{d.contact_name || phoneText(d.contact_phone)}</strong>
                    <span className="muted ao-num">
                      {d.reference} · {new Date(d.created_at).toLocaleDateString(locale, { timeZone: "Africa/Douala" })}
                    </span>
                  </span>
                  <span className="row">
                    {d.unread_client_messages ? (
                      <Badge tone="offer">{t("ee.client.unread", { count: d.unread_client_messages })}</Badge>
                    ) : null}
                    <Badge dot tone={d.status === "submitted" || d.status === "validated" ? "success" : d.status === "review" ? "info" : "default"}>
                      {t(`ee.status.${d.status}`)}
                    </Badge>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {page > 1 || hasNext ? (
            <Pager
              page={page}
              hasNext={hasNext}
              onChange={setPage}
              labels={{ previous: t("billing.previous"), next: t("billing.next"), page: t("billing.page", { page }) }}
            />
          ) : null}
        </GlassCard>
        <div className="bento__narrow">
          <NewDossier />
        </div>
      </div>
    </Page>
  );
}
